import { grey } from '@mui/material/colors';
import { createTheme } from '@mui/material/styles';
import {
  DEFAULT_VISUAL_OPTION,
  getVisualOption,
} from './design/visualOptions';

const BODY_FONT =
  '"IBM Plex Sans", "Helvetica Neue", "Helvetica", sans-serif';
const DISPLAY_FONT = BODY_FONT;
const UI_FONT = BODY_FONT;

const formComponentOverrides = (borderRadius) => ({
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontFamily: UI_FONT,
        borderRadius,
        boxShadow: 'none',
        transition:
          'transform 160ms ease, background-color 160ms ease, box-shadow 160ms ease',
        '&:hover': {
          boxShadow: 'none',
        },
      },
    },
  },
  MuiMenu: {
    styleOverrides: { paper: {
      backgroundImage: 'none', padding: 4,
      border: '1px solid var(--canvas-border)',
      boxShadow: '0 12px 36px rgba(0,0,0,0.16)',
    } },
  },
  MuiMenuItem: {
    styleOverrides: { root: {
      fontSize: '0.875rem', minHeight: 36, borderRadius: 5,
      '& .MuiSvgIcon-root': { fontSize: 18, color: 'var(--canvas-muted)' },
    } },
  },
  MuiTypography: {
    styleOverrides: {
      h1: {
        fontFamily: DISPLAY_FONT,
        color: 'var(--canvas-text)',
      },
      h2: {
        fontFamily: DISPLAY_FONT,
      },
      h3: {
        fontFamily: UI_FONT,
      },
      h4: {
        fontFamily: UI_FONT,
      },
      h5: {
        fontFamily: UI_FONT,
        fontSize: '1.2rem',
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
      size: 'medium',
    },
  },
  MuiFormControl: {
    styleOverrides: {
      root: {
        overflow: 'visible',
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius,
        backgroundColor: 'var(--canvas-field-bg, #fff)',
        transition:
          'background-color 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
        '&:hover': {
          backgroundColor: 'var(--canvas-field-hover, rgba(26,55,240,0.04))',
        },
        '&.Mui-focused': {
          boxShadow:
            '0 0 0 3px var(--canvas-focus-ring, rgba(26,55,240,0.28))',
        },
        '&.Mui-error': {
          boxShadow:
            '0 0 0 3px color-mix(in srgb, var(--canvas-error, #c62828) 22%, transparent)',
        },
      },
      input: {
        paddingTop: 'var(--canvas-field-pad-y, 12px)',
        paddingBottom: 'var(--canvas-field-pad-y, 12px)',
        paddingLeft: 'var(--canvas-field-pad-x, 14px)',
        paddingRight: 'var(--canvas-field-pad-x, 14px)',
        fontFamily: BODY_FONT,
      },
      notchedOutline: {
        borderColor: 'var(--canvas-border, rgba(26,31,55,0.12))',
        transition: 'border-color 160ms ease',
      },
    },
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        fontFamily: BODY_FONT,
        fontWeight: 500,
        color: 'var(--canvas-muted, #5c6478)',
        '&.Mui-focused': {
          color: 'var(--canvas-primary, #1a37f0)',
        },
      },
      // Canvas forms opt into external labels via .canvas-json-form CSS;
      // keep theme defaults sane for any non-form outlined fields.
      outlined: {
        lineHeight: 1.3,
      },
    },
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        fontFamily: BODY_FONT,
        marginLeft: 2,
        marginTop: 6,
        lineHeight: 1.35,
      },
    },
  },
  MuiFormControlLabel: {
    styleOverrides: {
      root: {
        marginLeft: 0,
        alignItems: 'center',
      },
      label: {
        fontFamily: BODY_FONT,
      },
    },
  },
  MuiSwitch: {
    styleOverrides: {
      switchBase: {
        '&.Mui-checked': {
          color: 'var(--canvas-primary, #1a37f0)',
        },
        '&.Mui-checked + .MuiSwitch-track': {
          backgroundColor: 'var(--canvas-primary, #1a37f0)',
        },
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        // When a title precedes content, MUI sets paddingTop: 0 — that clips
        // outlined InputLabels. Prefer tokenized padding for Canvas dialogs.
        '.MuiDialogTitle-root + &': {
          paddingTop: 'var(--canvas-dialog-body-pt, 22px)',
        },
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        paddingLeft: 'var(--canvas-dialog-body-px, 24px)',
        paddingRight: 'var(--canvas-dialog-body-px, 24px)',
        paddingBottom: 18,
        paddingTop: 8,
        gap: 'var(--canvas-dialog-actions-gap, 10px)',
      },
    },
  },
  MuiAccordion: {
    styleOverrides: {
      root: {
        borderRadius: `${borderRadius}px !important`,
        boxShadow: 'none',
        border: '1px solid var(--canvas-border, rgba(26,31,55,0.12))',
        '&:before': { display: 'none' },
        '&.Mui-expanded': {
          margin: 0,
          borderColor: 'var(--canvas-border-strong, rgba(26,55,240,0.35))',
          backgroundColor: 'var(--canvas-primary-soft, rgba(26,55,240,0.08))',
        },
      },
    },
  },
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        fontFamily: BODY_FONT,
      },
    },
  },
});

function buildBaseTheme(borderRadius) {
  return createTheme({
    typography: {
      fontFamily: BODY_FONT,
      h1: { fontSize: '2.5rem', fontWeight: 500 },
      h2: { fontSize: '2rem', fontWeight: 500 },
      button: { fontFamily: UI_FONT },
    },
    shape: {
      borderRadius,
    },
    components: formComponentOverrides(borderRadius),
  });
}

/**
 * @param {'light'|'dark'} mode
 * @param {string} [visualOptionId]
 */
export function createAppTheme(mode = 'light', visualOptionId = DEFAULT_VISUAL_OPTION) {
  const option = getVisualOption(visualOptionId);
  const tokens = option.mui[mode === 'dark' ? 'dark' : 'light'];
  const base = buildBaseTheme(tokens.borderRadius);

  if (mode === 'dark') {
    return createTheme(base, {
      palette: {
        mode: 'dark',
        primary: { main: tokens.primary },
        secondary: { main: tokens.secondary },
        text: {
          primary: '#ffffff',
          secondary: '#b0bec5',
        },
        background: {
          default: tokens.backgroundDefault,
          paper: tokens.backgroundPaper,
        },
        nodeBorder: grey[500],
        error: { main: '#ef5350' },
      },
      components: {
        MuiTypography: {
          styleOverrides: {
            h1: {
              color: tokens.primary,
            },
          },
        },
      },
    });
  }

  return createTheme(base, {
    palette: {
      mode: 'light',
      primary: { main: tokens.primary },
      secondary: { main: tokens.secondary },
      background: {
        default: tokens.backgroundDefault,
        paper: tokens.backgroundPaper,
      },
      nodeBorder: grey[800],
      error: { main: '#c62828' },
    },
  });
}

// Back-compat exports (default Studio option)
export const lightTheme = createAppTheme('light', DEFAULT_VISUAL_OPTION);
export const darkTheme = createAppTheme('dark', DEFAULT_VISUAL_OPTION);
